import React, { forwardRef } from "react";
import { cn } from "./cn";

/**
 * Bun card: white surface, hairline border, radius 12, shadow only when
 * floating (pass raised). Compose with Card.Header / Card.Section / Card.Row /
 * Card.Footer — separators are line-soft, never full line. Cards retain their
 * border, radius, and parent gutter at every viewport size.
 */
const Card = forwardRef(function Card({ as: Component = 'section', interactive = false, raised, selected = false, tone = "default", intensity = 0, borderTone = "default", borderStyle = "solid", mobileEmbedded = false, className, style, children, ...props }, ref) {
  return (
    <Component
      ref={ref}
      className={cn(
        "font-instrument text-bun-text",
        mobileEmbedded
          ? "bg-transparent border-0 rounded-none sm:bg-bun-surface sm:border sm:border-bun-line sm:rounded-bun-lg"
          : "bg-bun-surface border border-bun-line rounded-bun-lg",
        borderStyle === "dashed" && "border-dashed",
        tone === "subtle" && "bg-[color:color-mix(in_srgb,var(--bun-mist)_50%,var(--bun-surface))]",
        tone === "data-violet" && "bg-[color:color-mix(in_srgb,var(--bun-data-violet)_var(--bun-card-intensity),var(--bun-surface))]",
        borderTone === "strong" && "border-bun-line-strong",
        raised && "shadow-bun-raise border-bun-ink",
        selected && "border-bun-blue bg-bun-wash",
        interactive && "block text-left no-underline hover:border-bun-line-strong focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-bun-blue focus-visible:ring-offset-2",
        className
      )}
      style={tone === 'data-violet' ? {
        ...style,
        '--bun-card-intensity': `${8 + Math.max(0, Math.min(1, intensity)) * 32}%`,
      } : style}
      {...props}
    >
      {children}
    </Component>
  );
});
export default Card;

function Header({ title, meta, actions, variant = "default", wrapActions = false, stackActionsOnMobile = false, compactOnMobile = false, className, children }) {
  return (
    <div
      className={cn(
        "flex gap-[10px]",
        variant === "standalone" ? "py-0" : "px-[18px] py-[13px] border-b border-bun-line-soft",
        compactOnMobile && variant !== "standalone" && "py-1 sm:py-[13px]",
        variant === "section" && "bg-bun-mist border-t border-bun-line-soft",
        stackActionsOnMobile ? "flex-col items-stretch sm:flex-row sm:items-center" : "items-center",
        wrapActions && "flex-wrap",
        className
      )}
    >
      {(title || meta) && (
        <div className={cn("flex-1 min-w-0", wrapActions && "flex-auto shrink-0 max-w-full")}>
          {title && (
            <h2 className={cn("m-0 font-instrument font-semibold leading-snug tracking-normal", variant === "standalone" ? "text-bun-body" : "text-bun-heading")}>
              {title}
            </h2>
          )}
          {meta && <p className="m-0 mt-[3px] text-bun-meta font-book text-bun-stone">{meta}</p>}
        </div>
      )}
      {children}
      {actions && <div className={cn("ml-auto flex items-center gap-2", wrapActions && "max-w-full flex-wrap", stackActionsOnMobile && "w-full flex-wrap justify-end sm:w-auto")}>{actions}</div>}
    </div>
  );
}

function Section({ mobileUnpadded = false, compact = false, className, children, ...props }) {
  return (
    <div className={cn(mobileUnpadded ? (compact ? "sm:p-1" : "sm:px-[18px] sm:py-4") : (compact ? "p-1" : "px-[18px] py-4"), className)} {...props}>
      {children}
    </div>
  );
}

/** A hoverable list row inside a card (rows separated by line-soft). */
function Row({ as: Component = "div", className, interactive = true, highlighted = false, insetDivider = false, divider = "auto", layout = "flex", compactOnMobile = false, children, ...props }) {
  return (
    <Component
      className={cn(
        "px-[18px] py-3 border-b border-bun-line-soft",
        compactOnMobile && "py-1 sm:py-3",
        divider === "auto" && "last:border-b-0",
        layout === "flex" && "flex items-center gap-3",
        insetDivider && "relative border-b-0 after:pointer-events-none after:absolute after:inset-x-[18px] after:bottom-0 after:h-px after:bg-bun-line-soft last:after:hidden",
        interactive && "cursor-pointer hover:bg-bun-mist focus-within:bg-bun-mist",
        (Component === "button" || Component === "a") && "min-h-11 sm:min-h-0 focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-bun-blue",
        highlighted && "bg-bun-mist",
        className
      )}
      {...props}
    >
      {children}
    </Component>
  );
}

function Footer({ className, children }) {
  return (
    <div
      className={cn(
        "px-[18px] py-[11px] border-t border-bun-line-soft",
        className
      )}
    >
      {children}
    </div>
  );
}

Card.Header = Header;
Card.Section = Section;
Card.Row = Row;
Card.Footer = Footer;

/** Native disclosure keeps payloads and diagnostic detail keyboard accessible. */
function Disclosure({ title, defaultOpen = false, children, ...props }) {
  return <Card as="details" open={defaultOpen} {...props}>
    <summary className="min-h-11 cursor-pointer px-[18px] py-3 font-instrument text-bun-body font-strong text-bun-text focus-visible:outline focus-visible:outline-2 focus-visible:outline-bun-blue">
      {title}
    </summary>
    <Section className="border-t border-bun-line-soft">{children}</Section>
  </Card>;
}
Card.Disclosure = Disclosure;

/** Onboarding/action content: icon and copy above full-width actions on mobile. */
function ActionRow({ as: Component = 'div', id, title, description, icon: Icon, actions, children }) {
  return <Row as={Component} interactive={false} layout="block" id={id} aria-label={title}>
    <div className="flex flex-col gap-3 xl:flex-row xl:items-center">
      <div className="flex min-w-0 flex-1 items-center gap-[14px]">
        {Icon && <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-bun-lg bg-bun-tint-blue text-bun-blue-hover">
          <Icon className="h-5 w-5" aria-hidden="true" />
        </span>}
        <div className="min-w-0 flex-1">
          <h3 className="m-0 font-instrument text-bun-body font-semibold leading-snug tracking-normal">{title}</h3>
          <p className="m-0 mt-1 text-bun-body font-book leading-relaxed text-bun-slate">{description}</p>
        </div>
      </div>
      {actions && <div className="flex flex-wrap items-center gap-2 [&>*]:flex-1 xl:shrink-0 xl:[&>*]:flex-none">{actions}</div>}
    </div>
    {children && <div className="mt-3">{children}</div>}
  </Row>;
}
Card.ActionRow = ActionRow;
